import { parse as parseContentDisposition } from 'content-disposition';
import qs from 'qs';
import dayjs from '~/timeInit';

import {
  Request as RequestContract,
  Response,
  Tour as ResponseContract,
  paths,
} from '@luxuryescapes/contract-svc-tour';

import { ITEM_TYPE_BEDBANK } from '~/consts/order';

import { json_headers, request } from './common';
import { HttpResponse } from './types';

export type TourIndex = ResponseContract.TourIndex;
const ACCOMMODATION_ITEM_TYPE = 'accommodation';

type TravellerAccommodationDetails = {
  isLuxPreTourAccommodation?: boolean | null;
  isLuxPostTourAccommodation?: boolean | null;
  preAccommodationBookingNumber?: string | null;
  preAccommodationBookingDetails?: string | null;
  preAccommodationCheckInDate?: string | null;
  preAccommodationCheckOutDate?: string | null;
  postAccommodationBookingNumber?: string | null;
  postAccommodationBookingDetails?: string | null;
  postAccommodationCheckInDate?: string | null;
  postAccommodationCheckOutDate?: string | null;
};

type TravellerTransferStatusResponse = {
  arrivalTransferStatus: string;
  departureTransferStatus: string;
  has_transfers: boolean;
  has_arrival_transfer: boolean;
  has_departure_transfer: boolean;
};

interface IIndexResponse {
  status: number;
  message: string;
  result: TourIndex;
}

interface ITourResponse {
  status: number;
  message: string;
  result: ResponseContract.Tour;
}

export function basePath() {
  return window.configs.API_HOST + '/api/v2/tours';
}

function ordersBasePath() {
  return window.configs.API_HOST + '/api/orders';
}

export async function getTours(params: paths['/api/v2/tours']['get']['parameters']['query']): Promise<IIndexResponse> {
  const url = `${basePath()}?${qs.stringify(params)}`;

  return request(url, {
    method: 'GET',
  });
}

export async function getCustomerAccommodationOrders({
  page,
  per_page,
  brand,
  customer_id,
}: {
  page: number;
  per_page: number;
  brand: string;
  customer_id: string;
}) {
  const url = `${ordersBasePath()}?${qs.stringify({
    page,
    per_page,
    brand,
    customer_id,
    item_types: [ACCOMMODATION_ITEM_TYPE, ITEM_TYPE_BEDBANK],
    upcoming: true,
  })}`;

  return request(url, {
    method: 'GET',
  });
}

/**
 * A departure a by-departure cost price can be keyed against. `departureName`
 * is what svc-tour keys those prices by, and is null where a departure has
 * never been named.
 */
export type TourFormDeparture = Response.GetDeparturesForTourOption['departures'][number] & {
  /** Available when the departure has an active package departure in svc-tour. */
  packageDepartureId?: string;
};

/** The departures a tour still has ahead of it. */
export async function getTourFormDepartures(
  tourId: string,
  options?: { signal?: AbortSignal },
): Promise<Array<TourFormDeparture>> {
  const response = await request(`${basePath()}/forms/${tourId}/departures`, {
    method: 'GET',
    signal: options?.signal,
  });

  return response?.result?.departures ?? [];
}

export async function getTourById(id): Promise<ITourResponse> {
  const url = `${basePath()}/${id}`;
  return request(url, {
    method: 'GET',
  });
}

export async function updateTour(id, data): Promise<void> {
  const url = `${basePath()}/${id}`;
  const jsonData = JSON.stringify(data);

  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: jsonData,
  });
}

export async function bulkUpdateDepositAmount(brand, data): Promise<void> {
  const url = `${basePath()}/deposits/${brand}`;
  const jsonData = JSON.stringify(data);

  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: jsonData,
  });
}

interface GetBookingInfoResponse {
  status: number;
  message: string;
  result: App.TourReservationSnapshot;
}

export async function getBookingInfo(
  reservationId: string,
  options?: { includeCostData?: boolean },
): Promise<GetBookingInfoResponse> {
  const queryParams = options?.includeCostData ? '?includeCostData=true' : '';
  return request(basePath() + `/reservations/${reservationId}/snapshot${queryParams}`, {
    method: 'GET',
    headers: json_headers,
  });
}

export function getReservationMetaFields(reservationId) {
  return request(basePath() + `/reservations/${reservationId}/meta-fields`, {
    method: 'GET',
    headers: json_headers,
  });
}

export async function getCountriesVisitedList(): Promise<HttpResponse<{ countries: Array<string> }>> {
  return request(basePath() + `/filters/countries`, {
    method: 'GET',
    headers: json_headers,
  });
}

export function getTourCategoriesSuggestion(tourId: string) {
  return request(basePath() + `/${tourId}/categories/suggest`, {
    method: 'GET',
    headers: json_headers,
  });
}

export function downloadTourOptionPriceHistory(tourOptionId: string) {
  return request(basePath() + `/price-history/${tourOptionId}`, {
    method: 'GET',
    download: true,
  });
}

const MANIFEST_STATUS_MESSAGES: Record<number, string> = {
  400: 'Unable to download this manifest due to an invalid request',
  401: 'You are not authorised to download this manifest',
  403: 'You do not have permission to download this manifest',
  404: 'Manifest not found for this departure',
  500: 'Manifest generation failed. Please try again shortly',
  501: 'Manifest service not implemented. Please contact support',
  502: 'Manifest download failed due to a bad gateway. Please try again later',
  503: 'Manifest service is temporarily unavailable. Please try again shortly',
  504: 'Manifest download timed out. Please try again shortly',
};

const MANIFEST_FILENAME_PASSWORD_SEPARATOR = '___';

const getManifestStatusMessage = (status: number): string | undefined =>
  MANIFEST_STATUS_MESSAGES[status] ? `${MANIFEST_STATUS_MESSAGES[status]} (code ${status})` : undefined;

type FetchLikeResponse = globalThis.Response;

const getManifestErrorBody = async (response: FetchLikeResponse): Promise<string | null> => {
  try {
    const cloned = response.clone();
    const json = await cloned.json();
    if (json?.message) {
      return json.message as string;
    }
  } catch {
    // Fall through to text parsing below.
  }

  try {
    const text = await response.text();
    if (text) {
      return text;
    }
  } catch {
    // Ignore text parsing errors and fall back to status-based messaging.
  }

  return null;
};

export interface TourManifestFile {
  filename: string;
  password: string;
  blob: Blob;
}

export async function downloadManifestForDeparture(tourId: string, departureName: string): Promise<TourManifestFile> {
  const url = `${basePath()}/${encodeURIComponent(tourId)}/manifest/${encodeURIComponent(departureName)}`;

  const response: FetchLikeResponse = await request(url, {
    method: 'GET',
    headers: {
      Accept: 'application/zip, application/octet-stream',
    },
    returnResponse: true,
    allowNonOkResponse: true,
  });

  if (!response.ok) {
    const serverErrorMessage = await getManifestErrorBody(response);
    const statusMessage = getManifestStatusMessage(response.status);
    throw new Error(statusMessage || serverErrorMessage || `Unable to download manifest (status ${response.status}).`);
  }

  const header = response.headers.get('content-disposition');
  if (!header) {
    throw new Error('Manifest download response is missing file metadata.');
  }

  const parsed = parseContentDisposition(header);
  const rawFilename = parsed.parameters?.filename ?? '';
  const [password, filename] = rawFilename.split(MANIFEST_FILENAME_PASSWORD_SEPARATOR);

  if (!password || !filename) {
    throw new Error('Manifest download response has an unexpected filename format.');
  }

  const blob = await response.blob();

  return { filename, password, blob };
}

export interface ManifestViewData {
  overview: Record<string, string | number | boolean>;
  headings: Array<string>;
  data: Array<Record<string, string | number | boolean>>;
}

export async function viewManifestForDeparture(tourId: string, departureName: string): Promise<ManifestViewData> {
  const url = `${basePath()}/${encodeURIComponent(tourId)}/manifest/${encodeURIComponent(departureName)}/view`;

  const response = await request(url, {
    method: 'GET',
  });

  return response.result;
}

export function downloadReservationItineraryPDF(orderId: string, reservationId: string) {
  return request(basePath() + `/reservations/${reservationId}/order/${orderId}/itinerary-pdf`, {
    method: 'GET',
    download: true,
  });
}

export function sendReservationPortfolio(orderId: string, reservationId: string) {
  return request(basePath() + `/reservations/${reservationId}/order/${orderId}/send-portfolio`, {
    method: 'POST',
    headers: json_headers,
  });
}

export function copyTourContent(originalTourId: string, copyTourId: string) {
  return request(basePath() + `/forms/tour/${originalTourId}/copy-content`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      tourId: copyTourId,
    }),
  });
}

export function getDeparturesInsights(page?: number) {
  return request(basePath() + `/optimization/departures-for-optimization?page=${page}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function toTravellerAccommodationFields(accommodation?: TravellerAccommodationDetails) {
  return {
    is_lux_pre_tour_accommodation: accommodation?.isLuxPreTourAccommodation ?? false,
    is_lux_post_tour_accommodation: accommodation?.isLuxPostTourAccommodation ?? false,
    pre_accommodation_booking_number: accommodation?.preAccommodationBookingNumber ?? null,
    pre_accommodation_booking_details: accommodation?.preAccommodationBookingDetails ?? null,
    pre_accommodation_check_in_date: accommodation?.preAccommodationCheckInDate ?? null,
    pre_accommodation_check_out_date: accommodation?.preAccommodationCheckOutDate ?? null,
    post_accommodation_booking_number: accommodation?.postAccommodationBookingNumber ?? null,
    post_accommodation_booking_details: accommodation?.postAccommodationBookingDetails ?? null,
    post_accommodation_check_in_date: accommodation?.postAccommodationCheckInDate ?? null,
    post_accommodation_check_out_date: accommodation?.postAccommodationCheckOutDate ?? null,
  };
}

function toTravellerTransferStatus(transferStatus?: TravellerTransferStatusResponse) {
  if (!transferStatus) {
    return undefined;
  }

  return {
    arrivalTransferStatus: transferStatus.arrivalTransferStatus,
    departureTransferStatus: transferStatus.departureTransferStatus,
    hasTransfers: transferStatus.has_transfers,
    hasArrivalTransfer: transferStatus.has_arrival_transfer,
    hasDepartureTransfer: transferStatus.has_departure_transfer,
  };
}

export async function getTravellerDetails(bookingRef, withoutPassportPhoto = false, submittedOnly = true) {
  const response = await request(
    `${basePath()}/booking-number/${bookingRef}/traveller-details?submitted_only=${submittedOnly}&without_passport_photo=${withoutPassportPhoto}`,
    { method: 'GET' },
  );
  if (response.result?.travellers) {
    return response.result.travellers.map((traveller, index) => {
      const { transfer_status: transferStatus, ...travellerDetails } = traveller;
      const flightDepartureDate = response.result.travellerFormData?.[index].flightDetails?.departureDate;
      const flightArrivalDate = response.result.travellerFormData?.[index].flightDetails?.arrivalDate;
      const accommodation = response.result.travellerFormData?.[index].accommodation as
        | TravellerAccommodationDetails
        | undefined;

      return {
        ...travellerDetails,
        transferStatus: toTravellerTransferStatus(transferStatus),
        dob: traveller.dob ? dayjs(traveller.dob).format('YYYY-MM-DD') : null,
        passport_expiry: traveller.passport_expiry ? dayjs(traveller.passport_expiry).format('YYYY-MM-DD') : null,
        arrival_date: flightArrivalDate ? dayjs(flightArrivalDate).format('YYYY-MM-DD') : null,
        arrival_time: response.result.travellerFormData?.[index].flightDetails?.arrivalTime,
        departure_date: flightDepartureDate ? dayjs(flightDepartureDate).format('YYYY-MM-DD') : null,
        departure_time: response.result.travellerFormData?.[index].flightDetails?.departureTime,
        ...toTravellerAccommodationFields(accommodation),
      };
    });
  }

  return [];
}

export async function updateTravellerDetails(bookingRef, object, isDraft = true) {
  return request(basePath() + `/booking-number/${bookingRef}/traveller-details?isDraft=${isDraft}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(object),
  });
}

export function getItinerariesByDepartureForTourOption(tourId, tourOptionId) {
  return request(basePath() + `/${tourId}/tour-option/${tourOptionId}/itineraries-by-departure`, {
    method: 'GET',
    headers: json_headers,
  });
}

export function updateTourItinerariesByDeparture(tourId, tourOptionId, departureId, object) {
  return request(basePath() + `/${tourId}/tour-option/${tourOptionId}/departure/${departureId}/itineraries`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(object),
  });
}

export const getTourDeals = async (page: number, limit: number): Promise<Array<Response.TourDeal>> => {
  const response = await request(basePath() + `/deals`, {
    method: 'GET',
    headers: json_headers,
    query: {
      page: page,
      limit: limit,
    },
  });
  return response.result;
};

export const getTourDealById = async (id: string): Promise<Response.TourDeal> => {
  const response = await request(`${basePath()}/deals/${id}`, {
    method: 'GET',
    headers: json_headers,
  });
  return response.result;
};

export const deleteTourDealById = async (id: string): Promise<string> => {
  const response = await request(`${basePath()}/deals/${id}`, {
    method: 'DELETE',
  });
  return response.result;
};

export const createTourDeal = async (formValues: RequestContract.PostTourDealBody): Promise<Response.TourDeal> => {
  const response = await request(`${basePath()}/deals`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(formValues),
  });
  return response.result;
};

export const updateTourDeal = async (
  id: string,
  formValues: RequestContract.PutTourDealBody,
): Promise<Response.TourDeal> => {
  const response = await request(`${basePath()}/deals/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(formValues),
  });
  return response.result;
};
